"use client";

import React, { useSyncExternalStore } from "react";
import { Card, CardBody, CardHeader, CardFooter } from "@heroui/card";
import { FaApple, FaStar, FaRegStar } from "react-icons/fa";
import { IoLogoGooglePlaystore } from "react-icons/io5";

import { card, title as titleStyle } from "./primitives";
import Marquee from "./magicui/marquee";

// The marquee used to be `dynamic(..., { ssr: false })`, so the review text
// existed only in the RSC payload — crawlers that don't run scripts saw an
// empty box. It uses no browser API, so it now renders on the server. Only
// ONE copy of the cards is server-rendered (the review text, once); the extra
// copies that make the loop seamless are added after hydration, so the HTML
// doesn't carry the same 32 cards four times. The server-rendered copy stays
// paused until then, so all four copies start moving together (otherwise the
// late copies trail it and open a gap at the seam).
const noopSubscribe = () => () => {};

function useHydrated() {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false
  );
}

interface Review {
  Rating: number;
  Author: string;
  Title?: string;
  Detail: string;
  Date: string;
}

const ReviewCard: React.FC<Review> = ({
  Rating,
  Author,
  Title,
  Detail,
  Date,
}) => {
  const truncatedDetail =
    Detail.length > 200 ? `${Detail.substring(0, 200)}...` : Detail;

  return (
    <Card
      className={`w-[30rem] min-w-[30rem] h-80 ${card({ variant: "glass", size: "lg", bordered: false, elevation: "lg" })}`}
    >
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <div>
          {Title ? <FaApple size={30} /> : <IoLogoGooglePlaystore size={30} />}
        </div>
        <div className="flex flex-col">
          <p className="text-lg font-medium">{Author}</p>
          <p className="text-xs text-default-400">{Date}</p>
        </div>
      </CardHeader>
      <CardBody className="pt-4">
        {Title && <p className="text-lg font-bold mb-2">{Title}</p>}
        <p className="text-lg text-justify line-clamp-5">{truncatedDetail}</p>
      </CardBody>
      <CardFooter className="justify-end">
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, index) =>
            index < Rating ? (
              <FaStar key={index} className="text-yellow-500" size={20} />
            ) : (
              <FaRegStar key={index} className="text-yellow-500" size={20} />
            )
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

interface ReviewsClientProps {
  reviews: Review[];
  title: string;
  subtitle: string;
}

export function ReviewsClient({
  reviews,
  title,
  subtitle,
}: ReviewsClientProps) {
  const hydrated = useHydrated();

  if (reviews.length === 0) {
    return null;
  }

  return (
    <section className="py-16" id="reviews">
      <div className="container mx-auto px-4">
        <div className="section-title text-left mb-12">
          <h2 className={titleStyle({ fullWidth: true, size: "lg" })}>
            {title}
          </h2>
          <p className="text-xl lg:text-justify mt-4">{subtitle}</p>
        </div>
        <div className="relative h-96 overflow-hidden">
          <Marquee
            pauseOnHover
            className="[--duration:300s]"
            paused={!hydrated}
            repeat={hydrated ? 4 : 1}
          >
            {reviews.map((review, index) => (
              <ReviewCard key={index} {...review} />
            ))}
          </Marquee>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-1/5 bg-gradient-to-r from-transparent to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-1/5 bg-gradient-to-l from-transparent to-transparent" />
        </div>
      </div>
    </section>
  );
}
